import React, { useEffect } from 'react';
import Icon from './Icon';
import { janela } from '../services/plataforma';
import { formatarDuracao, ESPERA_ALMOCO_MIN, INTERVALO_REAVISO_MIN } from '../services/lembretePausa';

// ─────────────────────────────────────────────────────────────────────────────
// "Sua pausa continua marcada."
//
// O operador marcou Almoço, Café, Apoio ou Outro e não desmarcou. O tempo segue
// correndo no registro dele, e ninguém o avisa — foi exatamente o que aconteceu
// no navegador: o almoço terminou e o Orion ficou calado.
//
// Este modal é o segundo dos três canais do lembrete. O primeiro é a notificação
// do sistema, que sai da janela e alcança quem está longe da tela; o terceiro é
// o toast, para quem já está olhando. Este aqui cobre o meio do caminho: o
// operador voltou para a janela e precisa esbarrar no aviso, não caçá-lo.
//
// ─────────────────────────────────────────────────────────────────────────────
// POR QUE O BIP TOCA UMA VEZ SÓ
//
// O alerta de ociosidade apita sem parar até alguém clicar, e ali é o certo: a
// ociosidade não foi declarada, foi apurada, e significa que algo está errado
// agora. Uma pausa não é isso. Ela foi uma decisão do operador, e o lembrete
// existe para o caso de ele ter esquecido de desmarcá-la. Apitar em loop por
// causa de um almoço seria punição, não lembrete.
// ─────────────────────────────────────────────────────────────────────────────

const FREQUENCIA_HZ = 880;
const DURACAO_BIP_S = 0.24;
const VOLUME = 0.06;

export default function AlertaPausa({ tipo, rotulo = 'Pausa', minutos, aoVoltarAtivo, aoContinuar }) {
  const almoco = tipo === 'almoco';

  useEffect(() => {
    // A janela pode estar minimizada no botão flutuante: o modal não serve se
    // ninguém o vê. No navegador não há o que restaurar — quem faz esse papel
    // lá é a notificação do sistema.
    try { janela.restaurar(); } catch (e) { /* no navegador não há o que restaurar */ }

    const Contexto = window.AudioContext || window.webkitAudioContext;
    if (!Contexto) return undefined;

    let ctx;
    try { ctx = new Contexto(); } catch (e) { return undefined; }

    try {
      const osc = ctx.createOscillator();
      const ganho = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = FREQUENCIA_HZ;
      ganho.gain.value = VOLUME;
      osc.connect(ganho).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + DURACAO_BIP_S);
    } catch (e) { /* o navegador pode bloquear áudio sem interação prévia */ }

    return () => { try { ctx.close(); } catch (e) { /* já fechado */ } };
  }, []);

  return (
    <div className="drawer-overlay drawer-overlay-alto">
      <div className="modal-nx modal-estreito" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="drawer-title">
            {almoco ? 'Almoço em aberto' : `${rotulo} em aberto`}
            <span className="drawer-sub">há {formatarDuracao(minutos)}</span>
          </div>
        </div>

        <div className="drawer-body">
          <div className="panel panel-dest d-atual" style={{ marginBottom: 0 }}>
            <Icon name="coffee" size={16} style={{ marginRight: 8 }} />
            {almoco
              ? <>Seu almoço está marcado há <b>{formatarDuracao(minutos)}</b> e o tempo
                  continua correndo até você desmarcar.</>
              : <>Sua pausa de <b>{rotulo.toLowerCase()}</b> está marcada
                  há <b>{formatarDuracao(minutos)}</b>.</>}

            <div className="campo-dica" style={{ marginTop: 10 }}>
              {almoco
                ? `O lembrete começa depois de ${formatarDuracao(ESPERA_ALMOCO_MIN)} de almoço`
                : `O lembrete começa aos ${INTERVALO_REAVISO_MIN} minutos de pausa`}
              {` e volta a cada ${INTERVALO_REAVISO_MIN} minutos enquanto o status não mudar.`}
            </div>
          </div>
        </div>

        <div className="drawer-foot">
          {/* Continuar é uma escolha legítima — o almoço pode ter começado
              tarde. O botão existe para que ela seja explícita, e não um
              silêncio que o Orion interpreta sozinho. */}
          <button className="btn" onClick={aoContinuar}>
            Continuar na pausa
          </button>
          <button className="btn btn-primary" onClick={aoVoltarAtivo} autoFocus>
            <Icon name="check" size={14} /> Voltar para Ativo
          </button>
        </div>
      </div>
    </div>
  );
}
